import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, Paper, Typography, Modal, Box } from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";
import GuideOpenButton from "../../components/GuideOpenButton";
import { toast } from "react-toastify";

/**
 * Detecta si un video es de YouTube o externo
 * @param {string} video - Código de YouTube o URL externa
 * @returns {boolean} - true si es YouTube, false si es externo
 */
const isYouTubeVideo = (video) => {
  if (!video || typeof video !== 'string') return false;
  
  // Si es exactamente 11 caracteres alfanuméricos, es código de YouTube
  if (video.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(video)) {
    return true;
  }
  
  // Si contiene patrones de YouTube, es YouTube
  const youtubePatterns = [
    /youtube\.com/i,
    /youtu\.be/i
  ];
  
  return youtubePatterns.some(pattern => pattern.test(video));
};

/**
 * Obtiene la URL del thumbnail según el tipo de video
 * @param {string} video - Código de YouTube o URL externa
 * @param {string} thumbnail - URL de thumbnail personalizado (opcional)
 * @returns {string|null} - URL del thumbnail o null para placeholder
 */
const getVideoThumbnail = (video, thumbnail = null) => {
  // Si hay thumbnail personalizado, usarlo primero
  if (thumbnail && thumbnail.trim()) {
    return thumbnail.trim();
  }
  
  if (!video) return null;
  
  if (isYouTubeVideo(video)) {
    // Extraer código de YouTube si es URL
    let code = video;
    if (video.includes('youtube.com') || video.includes('youtu.be')) {
      const match = video.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
      if (match && match[1]) {
        code = match[1];
      } else if (video.length === 11) {
        code = video;
      }
    }
    return `https://img.youtube.com/vi/${code}/hqdefault.jpg`;
  }
  
  // Para videos externos sin thumbnail personalizado, retornar null (placeholder)
  return null;
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  mainPaperContainer: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 'calc(100vh - 200px)',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  mainPaper: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: theme.spacing(2),
    alignContent: 'start',
    '& > *': {
      minWidth: 0,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  helpPaper: {
    position: 'relative',
    minWidth: 0,
    maxWidth: '100%',
    padding: 0,
    boxShadow: theme.shadows[2],
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#fff',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  paperHover: {
    // Los estilos de hover se movieron al helpPaper
  },
  videoThumbnail: {
    width: '100%',
    height: '160px',
    minHeight: '160px',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    backgroundColor: '#f0f0f0',
    flexShrink: 0,
  },
  videoThumbnailPlaceholder: {
    width: '100%',
    height: '160px',
    minHeight: '160px',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '3rem',
    flexShrink: 0,
  },
  videoThumbnailContainer: {
    position: 'relative',
    width: '100%',
    height: '160px',
    minHeight: '160px',
    flexShrink: 0,
    overflow: 'hidden',
  },
  videoDuration: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '2px 4px',
    borderRadius: '2px',
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1,
  },
  videoContent: {
    padding: theme.spacing(1.5),
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  videoTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.25,
    marginBottom: theme.spacing(0.5),
    color: '#030303',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: 0,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  videoDescription: {
    fontSize: '0.75rem',
    lineHeight: 1.35,
    color: '#606060',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: 0,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  videoModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoModalContent: {
    outline: 'none',
    width: '90%',
    maxWidth: 1024,
    aspectRatio: '16/9',
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  videoErrorContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  videoErrorIcon: {
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
    opacity: 0.7,
  },
  videoErrorText: {
    fontSize: '1.2rem',
    marginBottom: theme.spacing(1),
  },
  videoErrorSubtext: {
    fontSize: '0.9rem',
    opacity: 0.8,
  },
}));

// Componente interno para manejar el thumbnail con estado de error
const VideoThumbnail = ({ video, thumbnail, classes }) => {
  const [thumbnailError, setThumbnailError] = useState(false);
  const thumbnailUrl = getVideoThumbnail(video, thumbnail);
  
  return (
    <div className={classes.videoThumbnailContainer}>
      {thumbnailUrl && !thumbnailError ? (
        <img
          src={thumbnailUrl}
          alt="Thumbnail"
          className={classes.videoThumbnail}
          loading="lazy"
          onError={() => setThumbnailError(true)}
        />
      ) : (
        <div className={classes.videoThumbnailPlaceholder}>
          ▶
        </div>
      )}
      <div className={classes.videoDuration}>
        ▶
      </div>
    </div>
  );
};

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
    
    // Refrescar datos cada 30 segundos para detectar cambios
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (video) => {
    // Validar que el video no esté vacío o null
    if (!video || !video.trim()) {
      toast.error(i18n.t("helps.messages.videoUnavailable"));
      return;
    }
    setSelectedVideo(video.trim());
    setVideoError(false); // Resetear error al abrir nuevo video
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setVideoError(false); // Resetear error al cerrar modal
  };

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") {
      closeVideoModal();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => {
      document.removeEventListener("keydown", handleModalClose);
    };
  }, [handleModalClose]);

  const renderVideoModal = () => {
    if (!selectedVideo) return null;
    
    const isYouTube = isYouTubeVideo(selectedVideo);
    
    // Extraer código de YouTube si es necesario
    let youtubeCode = selectedVideo;
    if (isYouTube && selectedVideo.length !== 11) {
      const match = selectedVideo.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
      if (match && match[1]) {
        youtubeCode = match[1];
      }
    }
    
    return (
      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          {videoError && !isYouTube ? (
            <Box className={classes.videoErrorContainer}>
              <Typography className={classes.videoErrorIcon}>
                <span role="img" aria-label="Advertencia">⚠️</span>
              </Typography>
              <Typography className={classes.videoErrorText}>
                {i18n.t("helps.messages.videoLoadError")}
              </Typography>
              <Typography className={classes.videoErrorSubtext}>
                {i18n.t("helps.messages.videoLoadHint")}
                <br />
                {i18n.t("helps.messages.videoCorsHint")}
              </Typography>
            </Box>
          ) : isYouTube ? (
            <iframe
              style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
              src={`https://www.youtube.com/embed/${youtubeCode}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onError={() => {
                setVideoError(true);
                toast.error(i18n.t("helps.messages.youtubeError"));
              }}
            />
          ) : (
            <video
              key={selectedVideo} // Forzar recreación cuando cambia la URL
              controls
              style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
              autoPlay
              onError={(e) => {
                setVideoError(true);
                toast.error(i18n.t("helps.messages.videoUrlError"));
                console.error('Error cargando video:', e);
              }}
              onLoadStart={() => setVideoError(false)}
              onCanPlay={() => setVideoError(false)}
            >
              <source src={selectedVideo} type="video/mp4" />
              <source src={selectedVideo} type="video/webm" />
              <source src={selectedVideo} type="video/ogg" />
              {i18n.t("helps.messages.videoNotSupported")}
            </video>
          )}
        </div>
      </Modal>
    );
  };

  const renderHelps = () => {
    return (
      <div className={classes.mainPaperContainer}>
        <div className={classes.mainPaper}>
          {records.length ? records.map((record, key) => (
            <Paper key={key} className={`${classes.helpPaper} ${classes.paperHover}`} onClick={() => openVideoModal(record.video)}>
              <VideoThumbnail video={record.video} thumbnail={record.thumbnail} classes={classes} />
              <div className={classes.videoContent}>
                <Typography component="div" className={classes.videoTitle}>
                  {record.title}
                </Typography>
                <Typography component="div" className={classes.videoDescription}>
                  {record.description || ''}
                </Typography>
              </div>
            </Paper>
          )) : null}
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("helps.title")} ({records.length})</Title>
        <MainHeaderButtonsWrapper>
          <GuideOpenButton id="helps" title={i18n.t("helps.title")} to="/helps" startId="watch" />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.root}>
        {renderHelps()}
      </div>
      {renderVideoModal()}
    </MainContainer>
  );
};

export default Helps;